import { useLangStore, useUserStore } from "@/entities"
import type { AxiosRequestConfig } from "axios"
import axios, { AxiosError } from "axios"
import { toast } from "sonner"
import type { SessionTokensDto } from "./generated"

export const apiInstance = axios.create({
  withCredentials: true,
  baseURL: import.meta.env.VITE_API_URL,
})

let refreshPromise: Promise<string | null> | null = null

const REFRESH_TIMEOUT_MS = 15_000

// Answers from /api/auth/refresh that mean "this session is dead". Only these
// end the client session; anything else (network error, timeout, 5xx/502
// during a redeploy) keeps the HttpOnly refresh cookie untouched and just
// fails the current request — the next request retries the refresh.
const DEFINITIVE_REFRESH_REJECTIONS = new Set([
  "INVALID_REFRESH_TOKEN",
  "REFRESH_TOKEN_REUSED",
  "REFRESH_TOKEN_ALREADY_ROTATED",
  "LEGACY_TOKEN_NOT_SUPPORTED",
  "SESSION_REVOKED",
  "REFRESH_TOKEN_REQUIRED",
  "WEB_LOGIN_REQUIRED",
])

export const isDefinitiveSessionRejection = (error: unknown): boolean => {
  const axiosError = error as AxiosError<{ message?: string | string[] }>
  const status = axiosError?.response?.status
  if (status !== 401 && status !== 403) return false
  const raw = axiosError.response?.data?.message
  const code = Array.isArray(raw) ? raw[0] : raw
  return typeof code === "string" && DEFINITIVE_REFRESH_REJECTIONS.has(code)
}

export const refreshBrowserSession = async (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise
  // This is the transport used by Orval itself. Calling the generated refresh
  // function here would re-enter createInstance and its 401 refresh handling.
  refreshPromise = apiInstance
    .post<SessionTokensDto>(
      "/api/auth/refresh",
      {},
      { timeout: REFRESH_TIMEOUT_MS }
    )
    .then(response => {
      const token = response.data.access_token
      useUserStore.getState().setAccessToken(token)
      return token
    })
    .catch((error: unknown) => {
      if (isDefinitiveSessionRejection(error)) {
        // The server already revoked the session / cleared the cookie.
        useUserStore.getState().logout({ revoke: false })
      } else {
        console.warn(
          "[api] refresh failed transiently, keeping session:",
          (error as AxiosError).response?.status ?? (error as Error).message
        )
      }
      return null
    })
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

const EXCLUDE_TOAST_ERROR_ROUTES: string[] = []

// The retried request was rejected with a fresh access token: the session
// itself is gone on the server (revoked between refresh and retry). The server
// already cleared the cookie, so do not ask it to revoke again.
const clearRejectedSession = () => {
  useUserStore.getState().logout({ revoke: false })
}

export const createInstance = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  try {
    const token = useUserStore.getState().access_token
    // TO IMPLEMENT LANGS
    const activeLang = useLangStore.getState().lang

    config.headers = {
      ...config.headers,
      "active-lang": activeLang ?? "en",
    }

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      }
    }

    const response = await apiInstance({
      ...config,
      ...options,
    })
    return response.data
  } catch (error) {
    const axiosError = error as AxiosError<{
      message: string
      error: string
      statusCode: number
    }>

    const retryConfig = config as AxiosRequestConfig & { _authRetry?: boolean }
    const failedAuthorization = retryConfig.headers?.Authorization
    const failedToken =
      typeof failedAuthorization === "string"
        ? failedAuthorization.replace(/^Bearer\s+/i, "")
        : undefined

    if (
      axiosError.response?.status === 401 &&
      failedToken &&
      !retryConfig._authRetry
    ) {
      retryConfig._authRetry = true
      const currentToken = useUserStore.getState().access_token
      const nextToken =
        currentToken && currentToken !== failedToken
          ? currentToken
          : await refreshBrowserSession()
      if (nextToken) {
        retryConfig.headers = {
          ...retryConfig.headers,
          Authorization: `Bearer ${nextToken}`,
        }
        try {
          const retryResponse = await apiInstance({
            ...retryConfig,
            ...options,
          })
          return retryResponse.data
        } catch (retryError) {
          if (
            axios.isAxiosError(retryError) &&
            retryError.response?.status === 401
          ) {
            clearRejectedSession()
          }
          throw retryError
        }
      }
    }

    if (axiosError.response) {
      const errorMessage = axiosError.response.data.message ?? ""
      if (
        errorMessage &&
        axiosError.response.status !== 401 &&
        !EXCLUDE_TOAST_ERROR_ROUTES.includes(config.url as string)
      ) {
        toast.error(errorMessage)
      }
    }

    throw error
  }
}

export type BodyType<Data> = Data
export type ErrorType<Error> = AxiosError<Error>
